import React, { useState, useEffect } from 'react';
import {
  CheckBox,
  Input,
  Select,
  Typography,
  Button
} from '../../../../../../components';
import configurationService from '../../../../../../api/configurationService';
import { useUi } from '../../../../../../context/ui.context';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  HelpText,
  TemplateForm,
  TemplateRow
} from './styles';

interface OpenStackFormData {
  auto_cleanup_images: boolean;
  auto_cleanup_number_of_days: number;
  auto_cleanup_image_types: string[];
  shelve_instances_on_suspend: boolean;
  allow_choosing_subnet: boolean;
  allow_choosing_public_network: boolean;
  add_instance_to_public_network_default: boolean;
  instance_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  image_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  volume_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  floating_ip_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  cluster_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  dns_domain_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  load_balancer_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  dbaas_instance_price_estimate_templates: Array<{
    language: string;
    content: string;
  }>;
  languages: Array<[string, string]>;
  cost_estimate_timespans: string[];
}

type TemplateType = 
  | 'instance_price_estimate_templates'
  | 'image_price_estimate_templates'
  | 'volume_price_estimate_templates'
  | 'floating_ip_price_estimate_templates'
  | 'cluster_price_estimate_templates'
  | 'dns_domain_price_estimate_templates'
  | 'load_balancer_price_estimate_templates'
  | 'dbaas_instance_price_estimate_templates';

const OpenstackPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<OpenStackFormData>({
    auto_cleanup_images: true,
    auto_cleanup_number_of_days: 14,
    auto_cleanup_image_types: ['ami', 'ari', 'aki', 'vhd', 'vmdk', 'raw', 'qcow2', 'vdi', 'iso'],
    shelve_instances_on_suspend: false,
    allow_choosing_subnet: true,
    allow_choosing_public_network: true,
    add_instance_to_public_network_default: true,
    instance_price_estimate_templates: [
      { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
      { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
    ],
    image_price_estimate_templates: [
      { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
      { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
    ],
    volume_price_estimate_templates: [
      { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
      { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
    ],
    floating_ip_price_estimate_templates: [
      { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
      { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
    ],
    cluster_price_estimate_templates: [
      { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
      { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
    ],
    dns_domain_price_estimate_templates: [
      { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
      { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
    ],
    load_balancer_price_estimate_templates: [
      { language: 'en', content: 'ESTIMATED COST<br>\n{{ cost_per_hour }} / hour<br>\nor {{ cost_per_billing_cycle }} / {{ billing_cycle_label|lower }}' }
    ],
    dbaas_instance_price_estimate_templates: [
      { language: 'en', content: 'ESTIMATED COST<br>\n{{ cost_per_hour }} / hour<br>\nor {{ cost_per_billing_cycle }} / {{ billing_cycle_label|lower }}' }
    ],
    languages: [
      ['uk', 'Ukrainian'] as [string, string],
      ['en', 'English'] as [string, string]
    ],
    cost_estimate_timespans: [
      '{{ cost_per_hour }}',
      '{{ cost_per_second }}',
      '{{ cost_per_minute }}',
      '{{ cost_per_day }}',
      '{{ cost_per_billing_cycle }}',
      '{{ billing_cycle_label }}'
    ]
  });

  const { viewAlert } = useUi();

  useEffect(() => {
    loadOpenStackConfiguration();
  }, []);

  const loadOpenStackConfiguration = async () => {
    try {
      setLoading(true);
      const response = await configurationService.getOpenstackConfigurations(1); // Используем ID 1 как пример
      console.log('API Response:', response);
      console.log('API Response data:', response.data);
      
      // Если API не работает, используем данные из JSON файла
      if (!response.data) {
        console.log('Using fallback data from JSON');
        const fallbackData = {
          auto_cleanup_images: true,
          auto_cleanup_number_of_days: 14,
          auto_cleanup_image_types: ['ami', 'ari', 'aki', 'vhd', 'vmdk', 'raw', 'qcow2', 'vdi', 'iso'],
          shelve_instances_on_suspend: false,
          allow_choosing_subnet: true,
          allow_choosing_public_network: true,
          add_instance_to_public_network_default: true,
          instance_price_estimate_templates: [
            { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
            { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
          ],
          image_price_estimate_templates: [
            { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
            { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
          ],
          volume_price_estimate_templates: [
            { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
            { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
          ],
          floating_ip_price_estimate_templates: [
            { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
            { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
          ],
          cluster_price_estimate_templates: [
            { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
            { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
          ],
          dns_domain_price_estimate_templates: [
            { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
            { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
          ],
          load_balancer_price_estimate_templates: [
            { language: 'en', content: 'ESTIMATED COST<br>\n{{ cost_per_hour }} / hour<br>\nor {{ cost_per_billing_cycle }} / {{ billing_cycle_label|lower }}' }
          ],
          dbaas_instance_price_estimate_templates: [
            { language: 'en', content: 'ESTIMATED COST<br>\n{{ cost_per_hour }} / hour<br>\nor {{ cost_per_billing_cycle }} / {{ billing_cycle_label|lower }}' }
          ],
          languages: [
            ['uk', 'Ukrainian'] as [string, string],
            ['en', 'English'] as [string, string]
          ],
          cost_estimate_timespans: [
            '{{ cost_per_hour }}',
            '{{ cost_per_second }}',
            '{{ cost_per_minute }}',
            '{{ cost_per_day }}',
            '{{ cost_per_billing_cycle }}',
            '{{ billing_cycle_label }}'
          ]
        };
        console.log('Setting fallback data:', fallbackData);
        setFormData(fallbackData);
      } else {
        console.log('Setting API data:', response.data);
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error loading OpenStack configuration:', error);
      // В случае ошибки также используем fallback данные
      const fallbackData = {
        auto_cleanup_images: true,
        auto_cleanup_number_of_days: 14,
        auto_cleanup_image_types: ['ami', 'ari', 'aki', 'vhd', 'vmdk', 'raw', 'qcow2', 'vdi', 'iso'],
        shelve_instances_on_suspend: false,
        allow_choosing_subnet: true,
        allow_choosing_public_network: true,
        add_instance_to_public_network_default: true,
        instance_price_estimate_templates: [
          { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
          { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
        ],
        image_price_estimate_templates: [
          { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
          { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
        ],
        volume_price_estimate_templates: [
          { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
          { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
        ],
        floating_ip_price_estimate_templates: [
          { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
          { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
        ],
        cluster_price_estimate_templates: [
          { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
          { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
        ],
        dns_domain_price_estimate_templates: [
          { language: 'en', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' },
          { language: 'uk', content: '{{ cost_per_hour }}-{{ cost_per_billing_cycle }}' }
        ],
        load_balancer_price_estimate_templates: [
          { language: 'en', content: 'ESTIMATED COST<br>\n{{ cost_per_hour }} / hour<br>\nor {{ cost_per_billing_cycle }} / {{ billing_cycle_label|lower }}' }
        ],
        dbaas_instance_price_estimate_templates: [
          { language: 'en', content: 'ESTIMATED COST<br>\n{{ cost_per_hour }} / hour<br>\nor {{ cost_per_billing_cycle }} / {{ billing_cycle_label|lower }}' }
        ],
        languages: [
          ['uk', 'Ukrainian'] as [string, string],
          ['en', 'English'] as [string, string]
        ],
        cost_estimate_timespans: [
          '{{ cost_per_hour }}',
          '{{ cost_per_second }}',
          '{{ cost_per_minute }}',
          '{{ cost_per_day }}',
          '{{ cost_per_billing_cycle }}',
          '{{ billing_cycle_label }}'
        ]
      };
      console.log('Setting error fallback data:', fallbackData);
      setFormData(fallbackData);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof OpenStackFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTemplateChange = (templateType: TemplateType, index: number, field: 'language' | 'content', value: string) => {
    console.log('handleTemplateChange called:', { templateType, index, field, value });
    console.log('Current formData before change:', formData[templateType]);
    
    setFormData(prev => {
      const templates = prev[templateType] as Array<{ language: string; content: string }>;
      const newTemplates = templates.map((template, i) => 
        i === index ? { ...template, [field]: value } : template
      );
      console.log('New templates:', newTemplates);
      const newFormData = {
        ...prev,
        [templateType]: newTemplates
      };
      console.log('New formData:', newFormData);
      return newFormData;
    });
  };

  const addTemplate = (templateType: TemplateType) => {
    setFormData(prev => {
      const templates = prev[templateType] as Array<{ language: string; content: string }>;
      return {
        ...prev,
        [templateType]: [...templates, { language: 'en', content: '' }]
      };
    });
  };

  const removeTemplate = (templateType: TemplateType, index: number) => {
    setFormData(prev => {
      const templates = prev[templateType] as Array<{ language: string; content: string }>;
      return {
        ...prev,
        [templateType]: templates.filter((_, i) => i !== index)
      };
    });
  };

  const handleSubmit = async () => {
    try {
      const configId = 1; // Временно хардкодим
      console.log('Saving OpenStack configuration:', formData);
      
      const response = await configurationService.updateOpenstackConfigurations(configId, formData);
      console.log('OpenStack configuration updated successfully:', response);
      
      // Можно добавить уведомление об успешном сохранении
      viewAlert({
        severity: 'information',
        message: 'Configuration saved successfully!',
        show: true
      });
    } catch (error: any) {
      console.error('Error saving OpenStack configuration:', error);
      viewAlert({
        severity: 'error',
        message: 'Error saving configuration. Please try again.',
        show: true
      });
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography variant="body">Loading OpenStack configuration...</Typography>
      </Container>
    );
  }

  const languageOptions = formData.languages.map(([code, name]) => ({
    id: code,
    title: name,
    label: name // Добавляем label для совместимости
  }));

  // Отладочная информация
  console.log('Current formData:', formData);
  console.log('Instance templates:', formData.instance_price_estimate_templates);
  console.log('Language options:', languageOptions);

  const supportedVariables = [
    '{{ cost_per_hour }}',
    '{{ cost_per_second }}',
    '{{ cost_per_minute }}',
    '{{ cost_per_day }}',
    '{{ cost_per_billing_cycle }}',
    '{{ billing_cycle_label }}'
  ];

  return (
    <Container>
      <Card>
        <CardHeader>
          <Typography variant="h1">OpenStack Configuration</Typography>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Automatic images cleanup */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Automatic images cleanup</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Automatically cleanup images"
                      value={formData.auto_cleanup_images}
                      onChange={() => handleInputChange('auto_cleanup_images', !formData.auto_cleanup_images)}
                    />
                    
                    <Input
                      title="Number of days to keep images"
                      placeholder="Number of days to keep images"
                      type="number"
                      value={formData.auto_cleanup_number_of_days}
                      handleChange={(value: any) => handleInputChange('auto_cleanup_number_of_days', parseInt(value))}
                    />
                    
                    <Input
                      title="Image types to cleanup"
                      placeholder="Image types to cleanup"
                      value={formData.auto_cleanup_image_types.join(', ')}
                      handleChange={(value: any) => handleInputChange('auto_cleanup_image_types', value.split(', ').map((s: string) => s.trim()))}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Automatically cleanup images</h3>
                      <p>Enables automatic cleanup of images for client.</p>
                      <p>You can specify the number of days to keep images starts from creation date of the image.</p>
                      <p>Once the number of days is reached the image will be deleted.</p>
                      <p>Valid image types for cleanup are: ami, ari, aki, vhd, vmdk, raw, qcow2, vdi and iso.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Suspend settings */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Suspend settings</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Shelve instances on suspend"
                      value={formData.shelve_instances_on_suspend}
                      onChange={() => handleInputChange('shelve_instances_on_suspend', !formData.shelve_instances_on_suspend)}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Shelve instances on suspend</h3>
                      <p>Normally when suspending a service all instances are powered off. If you check "Shelve instances on suspend" instances will be shelved when suspending a service.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Instance price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Instance price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.instance_price_estimate_templates.map((template, index) => {
                      console.log(`Rendering template ${index}:`, template);
                      console.log(`Template content:`, template.content);
                      console.log(`Template language:`, template.language);
                      return (
                        <TemplateForm key={index}>
                          <TemplateRow>
                            <Select
                              title="Language"
                              data={languageOptions}
                              selectedValue={template.language}
                              onChange={(value: any) => {
                                console.log('Language select onChange called with value:', value);
                                console.log('Current template:', template);
                                console.log('Language options:', languageOptions);
                                console.log('Selected value type:', typeof value);
                                console.log('Selected value:', value);
                                // value может быть id или строкой, в зависимости от того, как работает Select
                                const languageValue = typeof value === 'object' ? value.id : value;
                                handleTemplateChange('instance_price_estimate_templates', index, 'language', languageValue);
                              }}
                            />
                            <Input
                              title="Template"
                              placeholder="Template"
                              value={template.content || ''}
                              handleChange={(e: any) => {
                                console.log('Template input handleChange called with event:', e);
                                const value = e.target ? e.target.value : e;
                                console.log('Extracted value:', value);
                                handleTemplateChange('instance_price_estimate_templates', index, 'content', value);
                              }}
                              isTextArea={true}
                              customStyles={{width:'450px', height:'200px'}}
                              
                            />
                            <Button
                              title="Remove"
                              variant="stroke"
                              size="small"
                              onClick={() => removeTemplate('instance_price_estimate_templates', index)}
                            />
                          </TemplateRow>
                        </TemplateForm>
                      );
                    })}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('instance_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Instance price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on instance create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Image price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Image price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.image_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('image_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('image_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('image_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('image_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Image price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on image create, instance snapshot create & instance backup create forms.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Volume price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Volume price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.volume_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('volume_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('volume_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('volume_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('volume_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Volume price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on volume create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Floating IP price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Floating IP price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.floating_ip_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('floating_ip_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('floating_ip_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('floating_ip_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('floating_ip_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Floating IP price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on floating IP create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Cluster price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Cluster price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.cluster_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('cluster_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('cluster_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('cluster_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('cluster_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Cluster price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on cluster create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* DNS domain price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">DNS zone price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.dns_domain_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('dns_domain_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('dns_domain_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('dns_domain_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('dns_domain_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>DNS zone price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on DNS zone create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Load balancer price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Load balancer price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.load_balancer_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('load_balancer_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('load_balancer_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('load_balancer_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('load_balancer_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Load balancer price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on load balancer create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* DBaaS instance price estimate template */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">DBaaS instance price estimate template</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '2' }}>
                    {formData.dbaas_instance_price_estimate_templates.map((template, index) => (
                      <TemplateForm key={index}>
                        <TemplateRow>
                          <Select
                            title="Language"
                            data={languageOptions}
                            selectedValue={template.language}
                            onChange={(value: any) => {
                              const languageValue = typeof value === 'object' ? value.id : value;
                              handleTemplateChange('dbaas_instance_price_estimate_templates', index, 'language', languageValue);
                            }}
                          />
                          <Input
                            title="Template"
                            placeholder="Template"
                            value={template.content || ''}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleTemplateChange('dbaas_instance_price_estimate_templates', index, 'content', value);
                            }}
                            isTextArea={true}
                            customStyles={{width:'450px', height:'200px'}}
                            
                          />
                          <Button
                            title="Remove"
                            variant="stroke"
                            size="small"
                            onClick={() => removeTemplate('dbaas_instance_price_estimate_templates', index)}
                          />
                        </TemplateRow>
                      </TemplateForm>
                    ))}
                    <Button
                      title="Add new"
                      variant="primary"
                      size="small"
                      onClick={() => addTemplate('dbaas_instance_price_estimate_templates')}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>DBaaS instance price estimate template</h3>
                      <p>This is where you can configure templates for estimated cost text shown on DBaaS instance create form.</p>
                      <p>List of supported variables:</p>
                      <div className="variables-container">
                        {supportedVariables.map((variable, index) => (
                          <span key={index} className="fl-tag">{variable}</span>
                        ))}
                      </div>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button 
                title="Cancel" 
                variant="stroke" 
                size="small" 
                onClick={() => {}} 
              />
              <Button 
                title="Save Configuration" 
                variant="primary" 
                size="small" 
                onClick={handleSubmit} 
              />
            </div>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}

export default OpenstackPage;